import { useProductsSearched, useTrackEvent } from "@outschool/ui-analytics";
import { useServerExperimentContext } from "@outschool/ui-experiments";
import { useIsMobile } from "@outschool/ui-utils";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";

import { useSearchFilters } from "../../lib/useSearchFilters";

type ClassTrackSearchHookParams = {
  loading: boolean;
  exactListings: any[];
  itemsPerPage: number;
  currentUserHasLoaded: boolean;
};

type ClassTrackEventType =
  | "is_new_search"
  | "is_filter_change"
  | "is_browser_back";

export default function useClassTrackSearch({
  loading,
  exactListings,
  itemsPerPage,
  currentUserHasLoaded
}: ClassTrackSearchHookParams) {
  const { searchUid, filters } = useSearchFilters();
  const trackEvent = useTrackEvent();
  const trackProductsSearched = useProductsSearched();
  const [hasTrackedSearch, setHasTrackedSearch] = useState(false);
  const [oldFilters, setOldFilters] = useState(filters);
  const { loggedOutUserExperimentUid } = useServerExperimentContext();

  const history = useHistory();
  const [eventType, setEventType] =
    useState<ClassTrackEventType>("is_new_search");
  const mobileSearch = useIsMobile();

  useEffect(() => {
    if (!_.isEqual(filters, oldFilters)) {
      setOldFilters(filters);
      setHasTrackedSearch(false);
      setEventType("is_filter_change");
    }

    if (
      currentUserHasLoaded &&
      !loading &&
      exactListings &&
      !hasTrackedSearch
    ) {
      setHasTrackedSearch(true);
      const resultsWithSectionCount = exactListings.filter(
        ({ sectionCount }) => sectionCount > 0
      ).length;

      trackEvent("Class Search", {
        searchFilters: filters,
        experiments: {
          mobileSearch
        },
        resultsCount: exactListings.length,
        resultsWithSectionCount,
        resultsLimit: itemsPerPage,
        eventType: history.action === "POP" ? "is_browser_back" : eventType,
        searchUid,
        loggedOutUserExperimentUid
      });

      if (!!filters?.q) {
        trackProductsSearched({ query: filters.q });
      }
    }
  }, [
    currentUserHasLoaded,
    eventType,
    exactListings,
    filters,
    hasTrackedSearch,
    history,
    itemsPerPage,
    loading,
    loggedOutUserExperimentUid,
    mobileSearch,
    oldFilters,
    searchUid,
    trackEvent,
    trackProductsSearched
  ]);

  return { searchUid };
}
